import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { REPORTS } from "@/lib/reportConfig";
import { PageHeader, Loading, EmptyState, StatCard, StatusBadge } from "@/components/common";
import PeriodFilter, { usePeriod } from "@/components/PeriodFilter";
import { rupiah, numberID, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Printer, FileSpreadsheet, ChevronLeft, ChevronRight } from "lucide-react";

const LIMIT = 50;

function fmtVal(v, fmt) {
  if (v === null || v === undefined || v === "") return "-";
  if (fmt === "rp") return rupiah(v);
  if (fmt === "num") return numberID(v);
  if (fmt === "date") return formatDate(v);
  return String(v);
}

export default function Reports() {
  const { key } = useParams();
  const cfg = REPORTS[key];
  const { can } = useAuth();
  const navigate = useNavigate();
  const { period, start_date, end_date, setPeriod } = usePeriod(cfg?.defaultPeriod || "month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [mode, setMode] = useState("current");
  const [selects, setSelects] = useState({});
  const [opts, setOpts] = useState({});

  const params = useMemo(() => {
    const p = { period, page, limit: LIMIT };
    if (period === "custom") { p.start_date = start_date; p.end_date = end_date; }
    if (cfg?.modes) p.mode = mode;
    Object.entries(selects).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [period, start_date, end_date, page, mode, selects, cfg]);

  const pkey = JSON.stringify(params) + key;

  useEffect(() => { setPage(1); setSelects({}); setMode("current"); }, [key]);

  useEffect(() => {
    // load filter option sources
    (cfg?.filters || []).forEach((f) => {
      if (!f.source || opts[f.source]) return;
      const url = { suppliers: "/purchasing/suppliers", warehouses: "/inventory/warehouses", fabrics: "/masters/fabric_types", colors: "/masters/colors" }[f.source];
      if (url) api.get(url).then((r) => setOpts((o) => ({ ...o, [f.source]: r.data }))).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (!cfg || cfg.owner) return;
    if (period === "custom" && (!start_date || !end_date)) { setLoading(false); return; }
    setLoading(true);
    api.get(cfg.endpoint, { params }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkey]);

  useEffect(() => {
    if (!cfg?.owner) return;
    setLoading(true);
    const p = { period }; if (period === "custom") { p.start_date = start_date; p.end_date = end_date; }
    api.get(cfg.endpoint, { params: p }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, start_date, end_date, key]);

  if (!cfg) return <EmptyState title="Laporan tidak ditemukan" />;
  if (!can(cfg.perm)) return <EmptyState title="Akses ditolak" subtitle="Anda tidak memiliki izin untuk laporan ini." />;

  const columns = cfg.modes ? cfg.columnsByMode[mode] : cfg.columns;
  const summaryDef = cfg.modes ? cfg.summaryByMode[mode] : cfg.summary;
  const optLabel = (o) => o.nama || o.name || o.code || o.id;

  const exportData = async (format) => {
    try {
      const r = await api.get(cfg.endpoint, { params: { ...params, page: 1, limit: 100000 } });
      const rows = r.data.rows || [];
      if (rows.length === 0) return toast.error("Tidak ada data untuk diekspor");
      const headers = columns.map((c) => c.label);
      const lines = rows.map((row) => columns.map((c) => {
        const v = row[c.k]; const s = v === null || v === undefined ? "" : String(v);
        return `"${s.replace(/"/g, '""')}"`;
      }).join(","));
      const csv = [headers.join(","), ...lines].join("\n");
      const blob = new Blob(["\uFEFF" + csv], { type: format === "excel" ? "application/vnd.ms-excel" : "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${key}_${params.period}.${format === "excel" ? "xls" : "csv"}`;
      a.click();
      api.post("/reports/log-export", { report: key, format, period: params.period, filters: selects }).catch(() => {});
    } catch (e) { toast.error("Gagal ekspor"); }
  };

  const totalPages = data ? Math.max(1, Math.ceil((data.total || 0) / LIMIT)) : 1;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="report-title">{cfg.title}</h1>
          {!cfg.owner && <p className="text-sm text-slate-500 mt-1">Data transaksi nyata sesuai filter. {cfg.modes && mode === "current" ? "Posisi Stok Saat Ini (snapshot)." : "Periode Transaksi."}</p>}
        </div>
        <PeriodFilter period={period} start_date={start_date} end_date={end_date} onChange={setPeriod} />
      </div>

      {loading ? <Loading label="Memuat laporan..." /> : !data ? <EmptyState title="Data tidak tersedia" /> : cfg.owner ? (
        <OwnerReport data={data} />
      ) : (
        <>
          {/* Filters + Export */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {cfg.modes && (
              <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
                {[["current", "Posisi Stok Saat Ini"], ["movement", "Pergerakan (Periode)"]].map(([m, l]) => (
                  <button key={m} onClick={() => { setMode(m); setPage(1); }} data-testid={`mode-${m}`}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium ${mode === m ? "bg-indigo-600 text-white" : "text-slate-500"}`}>{l}</button>
                ))}
              </div>
            )}
            {(cfg.filters || []).map((f) => (
              <Select key={f.param} value={selects[f.param] || "__all"} onValueChange={(v) => { setSelects((s) => ({ ...s, [f.param]: v === "__all" ? "" : v })); setPage(1); }}>
                <SelectTrigger className="h-9 w-auto min-w-[150px]" data-testid={`filter-${f.param}`}><SelectValue placeholder={f.label} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">{f.label}: Semua</SelectItem>
                  {(f.options || opts[f.source] || []).map((o) => {
                    const val = typeof o === "string" ? o : o.id;
                    const lbl = typeof o === "string" ? o : optLabel(o);
                    return <SelectItem key={val} value={val}>{lbl}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            ))}
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={() => exportData("csv")} data-testid="export-csv"><Download className="mr-1.5 h-4 w-4" /> CSV</Button>
              <Button variant="outline" size="sm" onClick={() => exportData("excel")} data-testid="export-excel"><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel</Button>
              <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-btn"><Printer className="mr-1.5 h-4 w-4" /> Print</Button>
            </div>
          </div>

          {/* Summary */}
          {summaryDef && data.summary && (
            <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {summaryDef.map((s) => (
                <StatCard key={s.k} label={s.label} value={fmtVal(data.summary[s.k], s.fmt)} testId={`sum-${s.k}`} />
              ))}
            </div>
          )}

          {/* Table */}
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
            {data.rows.length === 0 ? <EmptyState title="Tidak ada data pada filter ini" /> : (
              <Table>
                <TableHeader><TableRow>{columns.map((c) => <TableHead key={c.k} className={["num", "rp"].includes(c.fmt) ? "text-right" : ""}>{c.label}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {data.rows.map((row, i) => (
                    <TableRow key={i} data-testid={`report-row-${i}`}>
                      {columns.map((c) => {
                        const isDrill = cfg.drill && cfg.drill.col === c.k;
                        const content = c.fmt === "badge" ? <StatusBadge status={row[c.k]} /> : fmtVal(row[c.k], c.fmt);
                        return (
                          <TableCell key={c.k} className={["num", "rp"].includes(c.fmt) ? "text-right tabular-nums" : ""}>
                            {isDrill ? <button onClick={() => navigate(cfg.drill.path)} className="font-mono text-xs font-semibold text-indigo-600 hover:underline">{row[c.k]}</button> : content}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Pagination */}
          {data.total > LIMIT && (
            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <span>Menampilkan {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, data.total)} dari {numberID(data.total)}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="page-prev"><ChevronLeft className="h-4 w-4" /></Button>
                <span className="px-2 py-1.5">Hal {page}/{totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="page-next"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function OwnerReport({ data }) {
  const Section = ({ title, items }) => (
    <div className="mb-6">
      <h3 className="mb-3 font-display text-lg font-semibold text-slate-900">{title}</h3>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        {items.map((it) => <StatCard key={it.label} label={it.label} value={it.value} hint={it.hint} testId={`owner-${it.label}`} />)}
      </div>
    </div>
  );
  return (
    <div>
      <p className="mb-4 text-xs text-slate-400">Metrik transaksi mengikuti periode. Nilai stok = Posisi Stok Saat Ini (snapshot).</p>
      <Section title="Purchasing (Periode Transaksi)" items={[
        { label: "Total PO", value: numberID(data.purchasing.total_po) },
        { label: "Total Nilai PO", value: rupiah(data.purchasing.total_value) },
        { label: "Outstanding PO", value: numberID(data.purchasing.outstanding), hint: "Snapshot" },
      ]} />
      <Section title="Produksi (Periode Transaksi)" items={[
        { label: "Target", value: numberID(data.production.target) },
        { label: "Actual", value: numberID(data.production.actual) },
        { label: "Achievement", value: `${data.production.achievement}%` },
        { label: "Reject", value: numberID(data.production.reject) },
        { label: "Rework", value: numberID(data.production.rework) },
      ]} />
      <Section title="Inventory (Posisi Stok Saat Ini)" items={[
        { label: "Low Stock", value: numberID(data.inventory.low_stock) },
        { label: "Out of Stock", value: numberID(data.inventory.out_of_stock) },
        { label: "Barang Jadi Menunggu Transfer", value: numberID(data.inventory.fg_waiting_transfer) },
      ]} />
    </div>
  );
}
